/**
 * Parse ISO 8601 duration format to human-readable string
 * Examples:
 * - P1M -> "1 month"
 * - P1Y -> "1 year"
 * - P1W -> "1 week"
 * - P7D -> "7 days"
 */
export const parseDuration = (duration: string | null): string => {
  if (!duration) return 'Unknown';

  const regex = /P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?/;
  const matches = duration.match(regex);

  if (!matches) return duration;

  const [, years, months, weeks, days] = matches;

  if (years) {
    const y = parseInt(years);
    return y === 1 ? '1 year' : `${y} years`;
  }

  if (months) {
    const m = parseInt(months);
    return m === 1 ? '1 month' : `${m} months`;
  }

  if (weeks) {
    const w = parseInt(weeks);
    return w === 1 ? '1 week' : `${w} weeks`;
  }

  if (days) {
    const d = parseInt(days);
    return d === 1 ? '1 day' : `${d} days`;
  }

  return duration;
};

/**
 * Get display text for trial duration
 * Returns null if no trial available
 */
export const getTrialDisplayText = (trialDuration: string | null): string | null => {
  if (!trialDuration) return null;
  
  const parsed = parseDuration(trialDuration);
  return `${parsed} free trial`;
};

/**
 * Convert ISO 8601 duration format to days (integer)
 * Examples:
 * - P7D -> 7
 * - P1W -> 7
 * - P1M -> 30 (approximate)
 * - P1Y -> 365 (approximate)
 * Returns undefined if duration is null or invalid
 */
export const durationToDays = (duration: string | null): number | undefined => {
  if (!duration) return undefined;

  const regex = /P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?/;
  const matches = duration.match(regex);

  if (!matches) return undefined;

  const [, years, months, weeks, days] = matches;

  let totalDays = 0;

  if (years) totalDays += parseInt(years) * 365;
  if (months) totalDays += parseInt(months) * 30;
  if (weeks) totalDays += parseInt(weeks) * 7;
  if (days) totalDays += parseInt(days);

  return totalDays > 0 ? totalDays : undefined;
};
